import { ErrorWithMetadata } from '@common/types';
import { BoxItem } from 'box-ui-elements/es';
import ConvertApi from 'convertapi-js';
import { isEmpty } from 'lodash';
import { useRetrieveConvertAPIToken } from 'hooks';
import { useBoxApi, useMutateBoxItems } from 'hooks/api/box';
import { getFileMetadata, getFileNameWithoutExtension, logger } from 'utils';

interface ConvertAndUploadOptions {
  boxFileId: string;
  convertTo: string;
  buildConvertParams?: (boxFile: BoxItem) => Record<string, string>;
}

export const useConvertApi = () => {
  const { retrieveConvertAPIToken } = useRetrieveConvertAPIToken();
  const { uploadFile, addFileMetadata, downloadFileAsBlob } = useMutateBoxItems();
  const { getBoxItemById } = useBoxApi();

  const getToken = async () => {
    const token = await retrieveConvertAPIToken();
    if (!token) {
      logger.log('error', {
        message: 'Failed to retrieve ConvertAPI token. Token is null',
      });
      throw new Error('Failed to obtain ConvertAPI token');
    }

    return token.token;
  };

  const convertAndUpload = async ({
    boxFileId,
    convertTo,
    buildConvertParams,
  }: ConvertAndUploadOptions): Promise<BoxItem> => {
    const token = await getToken();
    const convertApi = ConvertApi.auth(token);
    const params = convertApi.createParams();

    const boxFile = await getBoxItemById({ id: boxFileId, options: { includeMetadata: true } });
    if (!boxFile) {
      logger.log('error', {
        message: 'Failed to download file from Box',
        boxFileId,
        convertTo,
      });
      throw new Error(`Failed to download the file from Box`);
    }

    const boxFileBlob = await downloadFileAsBlob(boxFileId);

    params.add('file', new File([boxFileBlob], boxFile.name, { type: 'application/pdf' }));

    const convertParams = buildConvertParams?.(boxFile) ?? {};
    Object.entries(convertParams).forEach(([key, value]) => {
      params.add(key, value);
    });

    let files;

    try {
      const result = await convertApi.convert('pdf', convertTo, params);
      files = result.files;
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));

      const enrichedError = new ErrorWithMetadata('Failed to convert file', {
        message: error.message,
        name: error.name,
        stack: error.stack?.split('\n').slice(0, 3).join('\n'), // First 3 lines of stack
        convertTo,
        fileId: boxFileId,
        tokenStart: token.substring(0, 5),
      });

      throw enrichedError;
    }

    const blob = await fetch(files[0].Url).then((r) => r.blob());

    const uploadedFile = await uploadFile.mutateAsync({
      parentFolderId: boxFile.parent.id,
      fileName: files[0].FileName,
      file: blob,
      retryWithDate: true,
    });

    if (!uploadedFile) {
      logger.log('error', {
        message: 'Failed to upload the converted file',
        boxFileId,
      });
      throw new Error('Failed to upload the converted file');
    }

    const originalFileMetadata = getFileMetadata(boxFile);

    if (!isEmpty(originalFileMetadata)) {
      try {
        await addFileMetadata.mutateAsync({
          fileId: uploadedFile.id,
          metadata: originalFileMetadata,
        });
      } catch (e) {
        logger.log('warning', {
          message: 'Failed to add metadata to converted file',
          boxFileId,
          originalError: e,
        });
      }
    }
    return uploadedFile;
  };

  const clonePdfToDocx = async (boxFileId: string): Promise<BoxItem> =>
    convertAndUpload({
      boxFileId,
      convertTo: 'docx',
    });

  const redactContent = async (boxFileId: string, contentToRedact: string): Promise<BoxItem> =>
    convertAndUpload({
      boxFileId,
      convertTo: 'redact',
      buildConvertParams: (boxFile) => {
        const fileName = getFileNameWithoutExtension(boxFile.name);

        return {
          fileName: `${fileName}-redacted`,
          preset: 'manual',
          redactionColor: '#FFFFFF',
          redactionData: `[{ "Detect": "${contentToRedact}" }]`,
        };
      },
    });

  return { redactContent, clonePdfToDocx };
};
